import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useSavedRituals } from '../context/SavedRitualsContext';
import { getRecipe } from '../data/recipes';
import { Mail, Flame, Sparkles, ArrowRight, CheckCircle2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WeeklyDigest = () => {
  const { getWeeklyStats, getStreak, savedIds, getNote, completions } = useSavedRituals();
  const stats = getWeeklyStats();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const rituals = useMemo(() => {
    // rituals done this week, sorted by frequency
    return Object.entries(stats.byRitual)
      .map(([id, count]) => ({ recipe: getRecipe(id), count, streak: getStreak(id) }))
      .filter(x => x.recipe)
      .sort((a, b) => b.count - a.count);
  }, [stats, getStreak]);

  const savedWithoutActivity = savedIds
    .map(getRecipe)
    .filter(Boolean)
    .filter(r => !stats.byRitual[r.id]);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) {
      toast.error('Please enter a valid email.');
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API}/digest/subscribe`, {
        email,
        subscribed_at: new Date().toISOString(),
        active_rituals: savedIds,
      });
      setSubscribed(true);
      toast.success('You&rsquo;re on the list. Your first digest arrives Sunday morning.');
    } catch (err) {
      toast.error('Could not subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const heroDay = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-b from-[#efe9db] to-[#faf7f1]">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-4">
            <Calendar className="w-4 h-4" /> Weekly Ritual Digest
          </div>
          <h1 className="font-serif-elegant text-5xl md:text-7xl mb-3">Your Week in Rituals</h1>
          <p className="font-script text-2xl text-[#7a8b6b]">{heroDay}</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-4 mb-12">
          <StatCard icon={CheckCircle2} label="Rituals done" value={stats.totalCompletions} />
          <StatCard icon={Calendar} label="Active days" value={`${stats.activeDayCount}/7`} />
          <StatCard icon={Flame} label="Best streak" value={savedIds.length ? Math.max(0, ...savedIds.map(id => getStreak(id))) : 0} />
        </div>

        {rituals.length > 0 ? (
          <section className="mb-12">
            <h2 className="font-serif-elegant text-3xl mb-6">This week you practiced</h2>
            <ul className="space-y-4">
              {rituals.map(({ recipe, count, streak }) => {
                const note = getNote(recipe.id);
                return (
                  <li key={recipe.id} className="bg-[#faf7f1] border border-[#e5decf] p-4 md:p-5 flex flex-col sm:flex-row gap-4">
                    <Link to={`/recipes/${recipe.id}`} className="w-full sm:w-32 h-32 shrink-0 bg-[#efe9db] overflow-hidden">
                      <img src={recipe.image} alt={recipe.title} className="w-full h-full object-cover" />
                    </Link>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-1">
                        <Link to={`/recipes/${recipe.id}`} className="font-serif-elegant text-2xl link-underline">{recipe.title}</Link>
                        {streak > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs bg-[#7a3b2e]/10 text-[#7a3b2e] px-2 py-0.5">
                            <Flame className="w-3 h-3" /> {streak} day{streak > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-[#7a8b6b] mb-2">
                        Practiced {count} time{count > 1 ? 's' : ''} this week
                      </div>
                      {note && (
                        <blockquote className="mt-3 border-l-2 border-[#7a8b6b] pl-3 text-sm text-[#4a4640] italic line-clamp-3">
                          &ldquo;{note}&rdquo;
                        </blockquote>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : (
          <section className="mb-12 bg-[#efe9db] p-10 text-center">
            <Sparkles className="w-10 h-10 mx-auto text-[#7a8b6b] mb-4" />
            <p className="font-serif-elegant text-2xl mb-2">A gentle week</p>
            <p className="text-[#4a4640] mb-6">You haven&rsquo;t marked any rituals as done yet. That&rsquo;s okay &mdash; herbs meet you where you are.</p>
            <Link to="/recipes" className="btn-primary">Open the Recipe Journal</Link>
          </section>
        )}

        {savedWithoutActivity.length > 0 && (
          <section className="mb-12">
            <h2 className="font-serif-elegant text-2xl mb-4">Waiting for you</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {savedWithoutActivity.map(r => (
                <Link key={r.id} to={`/recipes/${r.id}`} className="group flex gap-3 p-3 bg-[#faf7f1] border border-[#e5decf] hover:shadow-md transition-shadow">
                  <div className="w-16 h-16 bg-[#efe9db] overflow-hidden shrink-0">
                    <img src={r.image} alt={r.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-elegant text-lg leading-tight mb-1">{r.title}</div>
                    <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#3a4a3b] group-hover:gap-2 transition-all">
                      Try it <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Email subscribe */}
        <section className="bg-[#3a4a3b] text-[#f6f2ea] p-10 md:p-14">
          <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#dcd6c7] mb-3">
            <Mail className="w-4 h-4" /> Weekly digest by email
          </div>
          <h2 className="font-serif-elegant text-3xl md:text-4xl mb-3">Get this in your inbox every Sunday</h2>
          <p className="text-[#e6e0d0] leading-relaxed max-w-2xl mb-6">
            A gentle Sunday-morning email with the rituals you practiced, streak highlights,
            and your journal notes from the week. Unsubscribe any time.
          </p>
          {subscribed ? (
            <div className="inline-flex items-center gap-2 text-[#dcd6c7]">
              <CheckCircle2 className="w-5 h-5" />
              <span>Subscribed. See you Sunday.</span>
            </div>
          ) : (
            <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 max-w-lg">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-transparent border border-[#dcd6c7]/40 px-4 py-3 text-sm placeholder:text-[#dcd6c7]/60 focus:outline-none focus:border-[#dcd6c7]"
                required
              />
              <button disabled={subscribing} className="btn-outline !border-[#dcd6c7] !text-[#dcd6c7] hover:!bg-[#dcd6c7] hover:!text-[#3a4a3b] disabled:opacity-60">
                {subscribing ? 'Signing up...' : 'Send me digests'}
              </button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-[#faf7f1] border border-[#e5decf] p-5 text-center">
    <Icon className="w-5 h-5 mx-auto text-[#3a4a3b] mb-2" />
    <div className="font-serif-elegant text-3xl md:text-4xl text-[#2f2a24] mb-1">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-[#6f6a5f]">{label}</div>
  </div>
);

export default WeeklyDigest;
